/*
 * Depth scale, the wireline sense of the word: 1:500 means one unit on screen
 * covers 500 units of well, so a plot at 1:500 is directly comparable with any
 * other plot at 1:500 -- which is the whole point of stating a scale.
 *
 * Ported from `useDepthScale.ts`/`DashboardToolbar.tsx` in the well-gold
 * monorepo (`packages/charts`), the same reference the track headers and the
 * crosshair came from. Two deliberate differences:
 *
 * - **Feet, not metres.** well-gold computes `PX_PER_CM * (100 / scale)`, which
 *   reads the depth as metres. Sw-height depths are feet throughout (`CURVE_UNITS`
 *   in `curveColors.ts`, the psi/ft gradient, the ~24,200 ft example well), so
 *   the conversion is per foot. Getting this wrong would not look broken -- it
 *   would silently label a 1:150 plot as 1:500.
 * - **No `native` mode.** well-gold's sizes the track to one pixel row per
 *   sample, which it needs for image logs. There are no image logs here, so
 *   `native` would just be an arbitrary scale with no denominator to state.
 */

/**
 * CSS pixels per centimetre, via the 96dpi reference pixel.
 *
 * An assumption rather than a measurement, and the same one every log viewer
 * makes: a browser cannot know the display's real pixel density, so 1:500 is
 * 1:500 on a nominal 96dpi screen. Zoom the browser and the paper scale moves
 * with it.
 */
export const PX_PER_CM = 96 / 2.54;

export const CM_PER_FOOT = 30.48;

/** Modes are the scale denominator as a string, or 'full' to fit the page. */
export type DepthScaleMode =
  | 'full'
  | '50'
  | '100'
  | '200'
  | '500'
  | '1000'
  | '2000';

/**
 * 1:200 and 1:500 are the conventional wireline plot scales; the rest bracket
 * them. Nothing below 1:50 -- the ~300 ft windows here would be 7,000 px at
 * 1:50 already -- and nothing above 1:2000, where the whole well is a couple of
 * hundred pixels and `full` is the better answer.
 */
export const DEPTH_SCALE_OPTIONS: { value: DepthScaleMode; label: string }[] = [
  { value: 'full', label: 'Full scale' },
  { value: '50', label: '1:50' },
  { value: '100', label: '1:100' },
  { value: '200', label: '1:200' },
  { value: '500', label: '1:500' },
  { value: '1000', label: '1:1000' },
  { value: '2000', label: '1:2000' }
];

export const pixelsPerFoot = (denominator: number): number =>
  (PX_PER_CM * CM_PER_FOOT) / denominator;

/**
 * The plot height a scale demands for a depth window, or `null` for 'full' --
 * meaning "no demand, fit the page".
 *
 * This is the height of the **plot area**, not of the chart box: the track's
 * top and bottom margins carry no depth, so including them would stretch the
 * denominator by however many pixels of chrome the track happens to have.
 *
 * The window is the *visible* one, so a drag-zoom at a fixed scale shortens the
 * track rather than re-scaling it. That is what keeps the scale a scale.
 */
export const scaledPlotHeight = (
  mode: DepthScaleMode,
  intervalFeet: number
): number | null => {
  if (mode === 'full') return null;
  const denominator = Number.parseFloat(mode);
  if (!Number.isFinite(denominator) || denominator <= 0) return null;
  if (!Number.isFinite(intervalFeet) || intervalFeet <= 0) return null;
  return Math.round(intervalFeet * pixelsPerFoot(denominator));
};
